// HUB DE LAYOUTS — todas as unidades pagadoras e o que cada uma fala, numa tela só.
//
// A versão anterior desta rota tinha três abas: "Por unidade" (um seletor que
// obrigava a escolher a unidade antes de ver qualquer coisa) e a documentação
// do padrão nos dois sentidos. A pergunta que a operação faz é outra —
// "quem usa o quê, e quem está com mudança agendada?" — e ela só se responde
// olhando TODAS as unidades ao mesmo tempo. Daí o hub (feature 008, US1):
//
//  1. UMA CONSULTA, FILTRO NA TELA. A resposta traz todas as unidades de uma
//     vez (300 cabem numa request) e os filtros são aplicados aqui, sobre o
//     dado já carregado. É o que faz filtro e busca responderem em menos de
//     200 ms (SC-007) sem uma ida ao servidor por tecla.
//  2. O FILTRO MORA NA URL (`?layout=&situacao=&responsavel=&q=`, com `?raiz=`
//     ainda aceito como alias do último). Abrir uma unidade
//     e voltar preserva o recorte por construção (FR-005), e o link é
//     compartilhável — "olha as unidades pendentes" é um link, não um roteiro
//     de cliques.
//  3. A CONTAGEM DE CADA CHIP É HONESTA: responde "quantos eu veria se
//     clicasse aqui", com os OUTROS filtros aplicados (padrão de `FilterChips`).
//  4. UNIDADE DE PROVA FICA FORA DAS CONTAGENS. Ela existe para provar layout,
//     não para pagar folha; somá-la aos cinco cartões inflaria a cobertura
//     real. Aparece à parte, rotulada, e pode ser filtrada.
//  5. O CABEÇALHO NÃO PULA. Ele é renderizado antes do dado chegar, com um
//     espaço estável para a competência; o que espera o dado é `Skeleton`.
//
// A documentação do padrão Atlas não some: vive na ficha de `atlas-retorno` e
// `atlas-movimento`, e o cabeçalho tem os dois atalhos.

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoErro,
  EstadoVazio,
  FilterBar,
  FilterChips,
  KpiCard,
  Pagination,
  Pill,
  Skeleton,
  type ChipOpcao,
  type Column,
  type PillVariant,
} from "@atlas/ui/web";
import type { LayoutSituacao, SituacaoArquivo } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import {
  aplicarFiltros,
  contagens,
  motivoVazio,
  paginaValida,
  recortar,
  OPCOES_POR_PAGINA,
  POR_PAGINA_PADRAO,
  type Predicado,
} from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { LayoutChip } from "../../components/LayoutChip";

// As formas da 008 saem do próprio método do cliente, e não de um import por
// nome: `packages/sdk/src/index.ts` ainda não reexporta `HubResposta` e
// companhia, e derivar do método é o padrão desta área (`layouts-ficha.tsx`,
// `trava-margem-monitor.tsx`). Continua certo no dia em que o reexport chegar.
type HubResposta = Awaited<ReturnType<typeof atlas.admin.layouts.hub>>;
type HubUnidade = HubResposta["unidades"][number];
type HubCatalogoItem = HubResposta["catalogo"][number];
type SituacaoCobertura = keyof HubResposta["cobertura"];
type AmbienteProva = keyof HubCatalogoItem["ultimaProva"];
type ResumoProva = NonNullable<HubCatalogoItem["ultimaProva"][AmbienteProva]>;

// ============================================================ rótulos

/** As cinco situações que CONTAM. "prova" fica de fora de propósito (ponto 4). */
const SITUACOES_CONTADAS: SituacaoCobertura[] = [
  "padrao",
  "adaptado",
  "compativel_disponivel",
  "compativel_rascunho",
  "pendente_inclusao",
];

const SITUACAO_ROTULO: Record<SituacaoCobertura, string> = {
  padrao: "No padrão Atlas",
  adaptado: "Adaptado",
  compativel_disponivel: "Compatível, não atribuído",
  compativel_rascunho: "Compatível em rascunho",
  pendente_inclusao: "Pendente de inclusão",
  prova: "De prova",
  teste: "Organização de teste",
};

// Só os pares texto/fundo já aprovados do `Pill` — nenhuma cor nova (FR-039).
const SITUACAO_PILL: Record<SituacaoCobertura, PillVariant> = {
  padrao: "expirado",
  adaptado: "emdia",
  compativel_disponivel: "aceita",
  compativel_rascunho: "pendente",
  pendente_inclusao: "rejeitada",
  prova: "expirado",
  teste: "expirado",
};

const SITUACAO_ACENTO: Record<SituacaoCobertura, "default" | "info" | "warn" | "success"> = {
  padrao: "default",
  adaptado: "info",
  compativel_disponivel: "success",
  compativel_rascunho: "warn",
  pendente_inclusao: "warn",
  prova: "default",
  teste: "default",
};

const SENTIDO_ROTULO = { entrada: "Retorno", saida: "Movimento" } as const;

const TIPO_TROCA: Record<string, string> = {
  retorno: "retorno",
  movimento: "movimento",
  carga_inicial: "carga inicial",
};

// Mesmos rótulos da Troca de arquivos: a última troca aqui é a mesma linha que
// aparece lá, e dois nomes para o mesmo estado é o que faz o operador procurar.
const TROCA_STATUS: Record<SituacaoArquivo, { rotulo: string; variant: PillVariant }> = {
  gerado: { rotulo: "gerado", variant: "averbado" },
  recebido: { rotulo: "recebido", variant: "pendente" },
  validando: { rotulo: "validando", variant: "pendente" },
  valido: { rotulo: "válido", variant: "emdia" },
  processado: { rotulo: "processado", variant: "aceita" },
  processado_com_ressalvas: { rotulo: "com ressalvas", variant: "pendente" },
  rejeitado: { rotulo: "rejeitado", variant: "rejeitada" },
};

const AMBIENTE_ROTULO: Record<AmbienteProva, string> = {
  local: "local",
  homolog: "homolog",
  producao: "produção",
};
const AMBIENTES: AmbienteProva[] = ["local", "homolog", "producao"];

/** "2026-09-03T…" → "03/09". Pela string, e não por `Date`, para não depender do fuso. */
function diaMes(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}` : iso;
}

/** Vigente OU agendada, em qualquer sentido — é o que "filtrar por layout" quer dizer (FR-002). */
function usaLayout(u: HubUnidade, codigo: string): boolean {
  return (
    u.entrada.codigo === codigo ||
    u.saida.codigo === codigo ||
    u.entrada.agendada?.codigo === codigo ||
    u.saida.agendada?.codigo === codigo
  );
}

/**
 * Um chip por organização RESPONSÁVEL (e nunca por atendida): "quem manda o
 * arquivo, e por quais folhas" é o corte, não a unidade solta.
 *
 * Era `prefeituraPaiId ?? id` (feature 009, T046). O vínculo antigo não tinha
 * vigência: uma autarquia que trocou de processador em março continuava
 * agrupada sob a matriz de sempre, e o hub — que é a tela de quem envia
 * arquivo — mostrava o arranjo do ano passado.
 */
const responsavelDe = (u: HubUnidade) => String(u.responsavelId ?? u.id);

const ESTILO_LINK_BOTAO: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid var(--border-strong)",
  background: "var(--surface)",
  color: "var(--text)",
  fontWeight: 600,
  fontSize: 13,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const ESTILO_CHIP_PROVA: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 999,
  border: "1px dashed var(--border-strong)",
  background: "transparent",
  color: "var(--text-muted)",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

// ============================================================ a tela

export function AdminLayoutsHub() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Rota antiga da 006 (`?unidade=<id>`): links salvos e o histórico do
  // navegador ainda apontam para ela. Redirecionar aqui, e não no router, é o
  // que mantém o redirecionamento junto de quem o entende.
  const unidadeLegada = params.get("unidade");
  const redirecionar = unidadeLegada != null && /^\d+$/.test(unidadeLegada);

  const filtros = {
    layout: params.get("layout") ?? "",
    situacao: params.get("situacao") ?? "",
    // `?raiz=` continua valendo como ALIAS de `?responsavel=`: o filtro mora na
    // URL justamente para caber num link, e link colado no chat do suporte não
    // pode morrer numa renomeação. O nome novo vence quando os dois vêm.
    responsavel: params.get("responsavel") ?? params.get("raiz") ?? "",
    q: params.get("q") ?? "",
  };

  const q = useQuery({
    queryKey: ["admin", "layouts", "hub"],
    queryFn: () => atlas.admin.layouts.hub(),
    enabled: !redirecionar,
  });

  const trocar = (patch: Partial<typeof filtros>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    // Mexer no recorte reescreve a URL com o nome novo e apaga o alias — senão
    // um `?raiz=` velho sobreviveria ao lado de um `?responsavel=` novo, e a
    // próxima leitura teria dois valores para a mesma pergunta.
    if ("responsavel" in patch) p.delete("raiz");
    setParams(p, { replace: true });
  };
  const limparFiltros = () => trocar({ layout: "", situacao: "", responsavel: "", q: "" });

  const unidades = q.data?.unidades ?? [];
  const catalogo = q.data?.catalogo ?? [];

  // Nome e situação por código: o vigente de uma unidade não carrega situação,
  // e a última troca só carrega o código. As duas coisas saem do catálogo.
  const porCodigo = useMemo(() => new Map(catalogo.map((c) => [c.codigo as string, c])), [catalogo]);
  const nomePorId = useMemo(() => new Map(unidades.map((u) => [u.id, u.nome])), [unidades]);
  /** Quem processa a própria folha — as candidatas a chip de responsável. */
  const responsaveis = useMemo(() => unidades.filter((u) => u.responsavelId == null), [unidades]);

  const predicados = useMemo<Record<string, Predicado<HubUnidade> | null>>(
    () => ({
      layout: filtros.layout ? (u) => usaLayout(u, filtros.layout) : null,
      situacao: filtros.situacao ? (u) => u.cobertura.situacao === filtros.situacao : null,
      responsavel: filtros.responsavel
        ? (u) => String(u.id) === filtros.responsavel || String(u.responsavelId) === filtros.responsavel
        : null,
      // A busca casa a sigla também: uma câmara ou autarquia é procurada pela
      // sigla ("A.M.T"), e o nome por extenso é o que ninguém digita.
      q: filtros.q ? (u) => matchAnyKeys(u, filtros.q, ["nome", "sigla"]) : null,
    }),
    [filtros.layout, filtros.situacao, filtros.responsavel, filtros.q],
  );

  const recorte = useMemo(() => aplicarFiltros(unidades, predicados), [unidades, predicados]);

  // PAGINACAO NO CLIENTE, sobre a lista ja filtrada. A resposta traz todas as
  // unidades numa request (ponto 1), entao paginar aqui e' fatiar — a busca
  // continua varrendo a base inteira, e nao a pagina, que e' a armadilha que o
  // `grids-inventario` documenta. A pagina volta a 1 quando o filtro muda:
  // ficar na 3 de uma lista que encolheu para 4 linhas parece defeito de dado.
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState<number>(POR_PAGINA_PADRAO);
  useEffect(() => {
    setPagina(1);
  }, [filtros.layout, filtros.situacao, filtros.responsavel, filtros.q]);
  const paginaAtual = paginaValida(pagina, recorte.length, porPagina);
  const paginado = useMemo(
    () => recortar(recorte, paginaAtual, porPagina),
    [recorte, paginaAtual, porPagina],
  );

  // Contagem de cada chip = a base com os OUTROS filtros aplicados (ponto 3).
  const chips = useMemo(() => {
    const baseSituacao = aplicarFiltros(unidades, predicados, "situacao");
    const baseLayout = aplicarFiltros(unidades, predicados, "layout");
    const baseResponsavel = aplicarFiltros(unidades, predicados, "responsavel");
    const porSituacao = contagens(baseSituacao, (u) => u.cobertura.situacao);
    const porResponsavel = contagens(baseResponsavel, responsavelDe);
    const situacao: ChipOpcao[] = [
      { value: "", label: "todas", count: baseSituacao.length },
      ...SITUACOES_CONTADAS.map((s) => ({ value: s, label: SITUACAO_ROTULO[s], count: porSituacao[s] ?? 0 })),
      { value: "prova", label: SITUACAO_ROTULO.prova, count: porSituacao.prova ?? 0 },
    ];
    const layout: ChipOpcao[] = [
      { value: "", label: "todos", count: baseLayout.length },
      ...catalogo.map((c) => ({
        value: c.codigo,
        label: c.nome,
        count: baseLayout.filter((u) => usaLayout(u, c.codigo)).length,
      })),
    ];
    const responsavel: ChipOpcao[] = [
      { value: "", label: "todas", count: baseResponsavel.length },
      ...responsaveis.map((r) => ({
        value: String(r.id),
        label: r.sigla || r.nome,
        count: porResponsavel[String(r.id)] ?? 0,
      })),
    ];
    return { situacao, layout, responsavel };
  }, [unidades, predicados, catalogo, responsaveis]);

  if (redirecionar) return <Navigate to={`/averbadora/layouts/unidade/${unidadeLegada}`} replace />;

  // O caminho de volta vai junto: a tela da unidade mostra "← Hub" com o
  // recorte que estava aberto, e `history.back()` chega ao mesmo lugar.
  const abrirUnidade = (id: number, ancora = "") =>
    navigate(`/averbadora/layouts/unidade/${id}${ancora}`, {
      state: { voltarPara: `${location.pathname}${location.search}` },
    });

  const situacaoDoCodigo = (codigo: string): LayoutSituacao | undefined => porCodigo.get(codigo)?.situacao;

  const colunasUnidades: Column<HubUnidade>[] = [
    {
      key: "unidade",
      header: "Unidade",
      wrap: true,
      render: (u) => (
        <CelulaUnidade
          u={u}
          responsavelNome={u.responsavelId != null ? nomePorId.get(u.responsavelId) : undefined}
        />
      ),
    },
    {
      key: "retorno",
      header: "Retorno",
      render: (u) => (
        <CelulaVigente
          vigente={u.entrada}
          situacao={situacaoDoCodigo(u.entrada.codigo)}
          deProva={u.deProva}
        />
      ),
    },
    {
      key: "movimento",
      header: "Movimento",
      render: (u) => (
        <CelulaVigente vigente={u.saida} situacao={situacaoDoCodigo(u.saida.codigo)} deProva={u.deProva} />
      ),
    },
    {
      key: "troca",
      header: "Última troca",
      render: (u) => <CelulaUltimaTroca troca={u.ultimaTroca} porCodigo={porCodigo} />,
    },
    {
      key: "cobertura",
      header: "Cobertura",
      render: (u) => (
        <span title={u.cobertura.motivo ?? SITUACAO_ROTULO[u.cobertura.situacao]}>
          <Pill variant={SITUACAO_PILL[u.cobertura.situacao]}>{SITUACAO_ROTULO[u.cobertura.situacao]}</Pill>
        </span>
      ),
    },
  ];

  const colunasCatalogo: Column<HubCatalogoItem>[] = [
    {
      key: "layout",
      header: "Layout",
      wrap: true,
      render: (c) => (
        <LayoutChip
          tamanho="sm"
          comLink
          nome={c.nome}
          codigo={c.codigo}
          versao={c.versao}
          situacao={c.situacao}
          padrao={c.padrao}
        />
      ),
    },
    {
      key: "sentido",
      header: "Sentido",
      // O arquivo da consignatária tem sentido "entrada" no catálogo, mas não é
      // retorno da folha: o mesmo rótulo da página da unidade.
      render: (c) => (c.finalidade === "entrada_consignataria" ? "Consignatária" : SENTIDO_ROTULO[c.sentido]),
    },
    {
      key: "usando",
      header: "Unidades usando",
      align: "right",
      render: (c) => {
        const n = c.unidadesUsando[c.sentido];
        // FR-007: o total a migrar aparece no catálogo, não só na linha da unidade.
        return c.situacao === "descontinuado" && n > 0 ? (
          <span title="unidades que ainda usam um layout descontinuado">
            {n}{" "}
            <span style={{ fontSize: 11, color: "var(--warn-500)", fontWeight: 600 }}>
              · migração pendente
            </span>
          </span>
        ) : (
          <>{n}</>
        );
      },
    },
    { key: "agendadas", header: "Agendadas", align: "right", render: (c) => <>{c.unidadesAgendadas}</> },
    {
      key: "prova",
      header: "Última prova",
      wrap: true,
      render: (c) => <CelulaUltimaProva provas={c.ultimaProva} />,
    },
  ];

  const vazio = motivoVazio(unidades.length, recorte.length);
  const provas = q.data?.cobertura.prova ?? 0;

  return (
    // `minmax(0, 1fr)` em toda grade desta tela (T081): a coluna implícita de
    // um grid é `auto`, que cresce até o conteúdo mais largo — uma tabela com
    // sete colunas empurraria a PÁGINA para a direita a 1024 px. Com o mínimo
    // em zero, quem rola é o quadro da tabela, e a página não rola (FR-038).
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {/* ---------------------------------------------------------- cabeçalho (ponto 5) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.1em",
              fontWeight: 700,
              color: "var(--text-dim)",
              textTransform: "uppercase",
            }}
          >
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Layouts</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 720 }}>
            Cada unidade pagadora, o layout que ela fala em cada sentido e o catálogo com o uso de cada
            layout.
          </p>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
            competência de referência{" "}
            {/* Mono e seis caracteres nos dois estados: o dado chega e nada se move. */}
            <code
              aria-busy={q.data ? undefined : "true"}
              style={{
                fontFamily: "var(--font-mono)",
                color: q.data ? "var(--text)" : "var(--text-dim)",
                fontWeight: 700,
              }}
            >
              {q.data?.competenciaReferencia ?? "AAAAMM"}
            </code>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* A ação vive à esquerda dos atalhos do padrão porque é a mais
              usada das três: quem abre o hub com um arquivo em mãos quer saber
              se ele entra, e não ler a especificação. */}
          <Button size="sm" variant="ghost" onClick={() => navigate("/averbadora/layouts/ensaio")}>
            Ensaiar um arquivo
          </Button>
          <Link to="/averbadora/layouts/atlas-retorno" style={ESTILO_LINK_BOTAO}>
            Padrão Atlas — retorno
          </Link>
          <Link to="/averbadora/layouts/atlas-movimento" style={ESTILO_LINK_BOTAO}>
            Padrão Atlas — movimento
          </Link>
        </div>
      </div>

      {q.isError ? (
        <EstadoErro mensagem="Não foi possível carregar o hub." onTentarDeNovo={() => q.refetch()} />
      ) : (
        <>
          {/* ---------------------------------------------------------- cobertura */}
          <section
            aria-labelledby="hub-cobertura"
            style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 id="hub-cobertura" style={{ margin: 0, fontSize: 16 }}>
                Cobertura
              </h2>
              {q.data ? (
                <button
                  type="button"
                  onClick={() => trocar({ situacao: filtros.situacao === "prova" ? "" : "prova" })}
                  aria-pressed={filtros.situacao === "prova"}
                  title="Unidades de prova existem para provar layouts, não para pagar folha: ficam fora das cinco contagens."
                  style={ESTILO_CHIP_PROVA}
                >
                  {provas} {provas === 1 ? "unidade" : "unidades"} de prova
                </button>
              ) : null}
            </div>
            {q.data ? (
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
                }}
              >
                {SITUACOES_CONTADAS.map((s) => (
                  <KpiCard
                    key={s}
                    label={SITUACAO_ROTULO[s]}
                    value={q.data.cobertura[s]}
                    accent={SITUACAO_ACENTO[s]}
                    cta={{ label: "filtrar", onClick: () => trocar({ situacao: s }) }}
                  />
                ))}
              </div>
            ) : (
              <div
                aria-busy="true"
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
                }}
              >
                {SITUACOES_CONTADAS.map((s) => (
                  <div
                    key={s}
                    style={{
                      border: "1px solid var(--border)",
                      borderRadius: 16,
                      padding: 20,
                      minHeight: 144,
                    }}
                  >
                    <Skeleton linhas={3} altura={22} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ---------------------------------------------------------- unidades */}
          <section
            aria-labelledby="hub-unidades"
            style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}
          >
            <h2 id="hub-unidades" style={{ margin: 0, fontSize: 16 }}>
              Unidades
            </h2>
            {q.data ? (
              <FilterBar
                searchValue={filtros.q}
                onSearchChange={(v) => trocar({ q: v })}
                searchPlaceholder="Buscar unidade pelo nome"
                // O rótulo fica visualmente oculto, mas existe: com um valor
                // dentro do campo o placeholder some, e sem rótulo quem usa
                // leitor de tela perde o que aquela caixa busca (FR-039).
                searchLabel="Buscar unidade pelo nome"
                onReset={limparFiltros}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <FilterChips
                    label="Situação"
                    value={filtros.situacao}
                    options={chips.situacao}
                    onChange={(v) => trocar({ situacao: v })}
                  />
                  <FilterChips
                    label="Layout"
                    value={filtros.layout}
                    options={chips.layout}
                    onChange={(v) => trocar({ layout: v })}
                  />
                  <FilterChips
                    label="Responsável"
                    value={filtros.responsavel}
                    options={chips.responsavel}
                    onChange={(v) => trocar({ responsavel: v })}
                  />
                </div>
              </FilterBar>
            ) : null}
            {q.data ? (
              <p aria-live="polite" style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
                {recorte.length} de {unidades.length} {unidades.length === 1 ? "unidade" : "unidades"}
              </p>
            ) : null}
            <DataTable
              caption="Unidades pagadoras e o layout em vigor por sentido"
              loading={!q.data}
              columns={colunasUnidades}
              rows={paginado.itens}
              rowKey={(u) => String(u.id)}
              emptyState={
                vazio === "sem-dados" ? (
                  <EstadoVazio
                    titulo="Nenhuma unidade pagadora ainda."
                    orientacao="Cadastre em Prefeituras."
                    acao={{
                      rotulo: "ir para Órgãos e entidades",
                      onClick: () => navigate("/averbadora/organizacoes"),
                    }}
                  />
                ) : (
                  <EstadoVazio
                    titulo="Nenhuma unidade com esses filtros"
                    orientacao="A busca e os chips acima estão cortando tudo."
                    acao={{ rotulo: "limpar filtros", onClick: limparFiltros }}
                  />
                )
              }
              actions={(u) => (
                <span style={{ display: "inline-flex", gap: 6 }}>
                  <Button size="sm" variant="ghost" onClick={() => abrirUnidade(u.id)}>
                    Abrir unidade
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => abrirUnidade(u.id, "#formato-declarado")}>
                    Formato declarado
                  </Button>
                </span>
              )}
            />
            {q.data && recorte.length > 0 ? (
              <Pagination
                total={recorte.length}
                pagina={paginaAtual}
                porPagina={porPagina}
                onPagina={setPagina}
                onPorPagina={(n) => {
                  setPorPagina(n);
                  setPagina(1);
                }}
                opcoes={OPCOES_POR_PAGINA}
                rotulo="unidades"
              />
            ) : null}
          </section>

          {/* ---------------------------------------------------------- catálogo */}
          <section
            aria-labelledby="hub-catalogo"
            style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}
          >
            <h2 id="hub-catalogo" style={{ margin: 0, fontSize: 16 }}>
              Catálogo
            </h2>
            <DataTable
              caption="Catálogo de layouts"
              loading={!q.data}
              columns={colunasCatalogo}
              rows={catalogo}
              rowKey={(c) => c.codigo}
              emptyState={
                <EstadoVazio
                  titulo="Nenhum layout no catálogo."
                  orientacao="Incluir um layout é trabalho de engenharia, pelo roteiro de inclusão."
                />
              }
              actions={(c) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/averbadora/layouts/${encodeURIComponent(c.codigo)}`)}
                >
                  Ficha
                </Button>
              )}
            />
          </section>

          {/* ---------------------------------------------------------- fila de inclusão */}
          {q.data && q.data.pendentesInclusao.length > 0 ? (
            <Card>
              <h2 style={{ margin: "0 0 6px", fontSize: 16 }}>Fila de inclusão</h2>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-muted)" }}>
                Unidades cujo formato declarado nenhum layout do catálogo cobre. Cada uma entra pelo roteiro
                de inclusão (
                <code style={{ fontFamily: "var(--font-mono)" }}>
                  specs/domain/roteiro-inclusao-layout.md
                </code>
                ): rascunho, exemplo, prova de ponta a ponta e só então disponível.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6, fontSize: 13 }}>
                {q.data.pendentesInclusao.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/averbadora/layouts/unidade/${p.id}`}
                      state={{ voltarPara: `${location.pathname}${location.search}` }}
                    >
                      {p.nome}
                    </Link>
                    <span style={{ color: "var(--text-muted)" }}> — {p.motivo}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

// ============================================================ células

/**
 * A faixa de migração pendente fica DENTRO da célula porque a `DataTable` não
 * estiliza linha. As margens negativas anulam o padding da célula para a
 * faixa correr a altura toda, como se fosse da linha.
 */
function CelulaUnidade({ u, responsavelNome }: { u: HubUnidade; responsavelNome?: string }) {
  const atendida = u.responsavelId != null;
  return (
    <div
      style={{
        display: "grid",
        gap: 4,
        margin: "-10px 0 -10px -12px",
        padding: "10px 0 10px 9px",
        borderLeft: u.migracaoPendente ? "3px solid var(--warn-500)" : "3px solid transparent",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {atendida ? (
          <span
            style={{ paddingLeft: 14, color: "var(--text)" }}
            title={responsavelNome ? `folha processada por ${responsavelNome}` : undefined}
          >
            <span aria-hidden="true">↳ </span>
            {u.nome}
          </span>
        ) : (
          <strong>{u.nome}</strong>
        )}
        {u.deProva ? <Pill variant="expirado">unidade de prova</Pill> : null}
      </span>
      {/* O QUE A UNIDADE É, e não só como ela se chama. Sem isto, "I.M.A.M.N."
          e "Morada Nova" são dois nomes no mesmo nível visual, e nada na tela
          diz que um é instituto de previdência e o outro é a prefeitura. */}
      <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
        {u.sigla ? <span style={{ fontFamily: "var(--font-mono)", marginRight: 6 }}>{u.sigla}</span> : null}
        {u.rotuloTipo}
      </span>
      {u.migracaoPendente ? (
        <span
          style={{ fontSize: 11, fontWeight: 700, color: "var(--warn-500)" }}
          title="usa um layout descontinuado em algum sentido"
        >
          migração pendente
        </span>
      ) : null}
    </div>
  );
}

function CelulaVigente({
  vigente,
  situacao,
  deProva,
}: {
  vigente: HubUnidade["entrada"];
  situacao: LayoutSituacao | undefined;
  deProva: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <LayoutChip
        tamanho="sm"
        comLink
        nome={vigente.nome}
        codigo={vigente.codigo}
        versao={vigente.versao}
        situacao={situacao}
        padrao={vigente.padrao}
        emTeste={deProva && situacao === "rascunho"}
      />
      {vigente.agendada ? (
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          a partir de {vigente.agendada.vigenciaCompetencia}:{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>{vigente.agendada.codigo}</code>
        </span>
      ) : null}
    </div>
  );
}

/**
 * "nenhuma troca ainda" em vez de célula vazia (FR-006): uma unidade recém
 * cadastrada está no caso certo, não num estado quebrado.
 */
function CelulaUltimaTroca({
  troca,
  porCodigo,
}: {
  troca: HubUnidade["ultimaTroca"];
  porCodigo: Map<string, HubCatalogoItem>;
}) {
  if (!troca) return <span style={{ color: "var(--text-muted)" }}>nenhuma troca ainda</span>;
  const layout = troca.layoutCodigo ? porCodigo.get(troca.layoutCodigo) : undefined;
  const status = TROCA_STATUS[troca.status as SituacaoArquivo] ?? {
    rotulo: troca.status,
    variant: "pendente" as PillVariant,
  };
  const sep = (
    <span aria-hidden="true" style={{ color: "var(--text-dim)" }}>
      ·
    </span>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12 }}>
      <code style={{ fontFamily: "var(--font-mono)" }}>{troca.competencia}</code>
      {sep}
      <span>{TIPO_TROCA[troca.tipo] ?? troca.tipo}</span>
      {troca.layoutCodigo ? (
        <>
          {sep}
          <LayoutChip
            tamanho="sm"
            nome={layout?.nome ?? troca.layoutCodigo}
            codigo={troca.layoutCodigo}
            versao={troca.layoutVersao ?? layout?.versao ?? "—"}
            situacao={layout?.situacao}
            padrao={layout?.padrao ?? false}
          />
        </>
      ) : null}
      {sep}
      <Pill variant={status.variant}>{status.rotulo}</Pill>
    </span>
  );
}

/**
 * Três pílulas, uma por ambiente, e "nunca provado" onde não houve prova. A
 * coluna existe antes de a prova existir, por desenho: é ela que torna visível
 * o que ainda falta provar.
 */
function CelulaUltimaProva({ provas }: { provas: Record<AmbienteProva, ResumoProva | null> }) {
  return (
    <span style={{ display: "inline-flex", gap: 10, flexWrap: "wrap" }}>
      {AMBIENTES.map((a) => {
        const p = provas[a];
        const rotulo = AMBIENTE_ROTULO[a];
        let pilula: { variant: PillVariant; texto: string; title: string };
        if (!p)
          pilula = {
            variant: "expirado",
            texto: "nunca provado",
            title: `${rotulo}: nenhuma prova registrada`,
          };
        else if (p.resultado === "verde")
          pilula = {
            variant: "aceita",
            texto: `verde · ${diaMes(p.terminadoEm)}`,
            title: `${rotulo}: verde na versão ${p.layoutVersao}`,
          };
        else
          pilula = {
            variant: "rejeitada",
            texto: `falha · ${diaMes(p.terminadoEm)}`,
            title: p.passoDaFalha ?? `${rotulo}: falha`,
          };
        return (
          <span key={a} title={pilula.title} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{rotulo}</span>
            <Pill variant={pilula.variant}>{pilula.texto}</Pill>
          </span>
        );
      })}
    </span>
  );
}
